import { memo, useCallback, useEffect, useId, useState, type ChangeEvent, type ReactNode } from 'react'
import { draftMatches, invalidRecipients, parseRecipientList, type RecipientKind } from '../../lib/recipientList.js'

interface RecipientListFieldProps {
  label: string
  kind: RecipientKind
  value: string[]
  placeholder?: string
  /** Aide sous le champ ; le compte et les erreurs s'y ajoutent. */
  hint?: ReactNode
  onChange: (entries: string[]) => void
}

const MAX_TARGETS = 20

/**
 * Liste de destinataires en champ texte : une entrée par ligne (virgule et
 * point-virgule acceptés). Le texte tapé est conservé tel quel — voir
 * `lib/recipientList.ts` pour la raison — et le compte ainsi que les entrées
 * invalides sont affichés AVANT l'enregistrement, que le serveur refuserait
 * sinon d'un bloc.
 */
export const RecipientListField = memo(function RecipientListField({
  label, kind, value, placeholder, hint, onChange,
}: RecipientListFieldProps) {
  const [draft, setDraft] = useState(() => value.join('\n'))
  const hintId = useId()

  // Liste modifiée hors du champ (bouton « Ajouter au rapport », rechargement) :
  // seul ce cas réécrit le brouillon.
  useEffect(() => {
    setDraft(current => draftMatches(current, value) ? current : value.join('\n'))
  }, [value])

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(event.target.value)
    onChange(parseRecipientList(event.target.value))
  }, [onChange])

  const invalid = invalidRecipients(value, kind)
  const noun = kind === 'email' ? 'adresse' : 'conversation'
  const tooMany = value.length > MAX_TARGETS

  return (
    <label>
      <span>{label}</span>
      <textarea
        className="tp-input"
        rows={Math.min(Math.max(value.length + 1, 3), 8)}
        placeholder={placeholder}
        value={draft}
        onChange={handleChange}
        aria-describedby={hintId}
        aria-invalid={invalid.length > 0 || tooMany}
        spellCheck={false}
        autoCapitalize="off"
      />
      <span id={hintId} className="settings-recipient-hint">
        <span>
          {value.length === 0 ? `Aucune ${noun}` : `${value.length} ${noun}${value.length > 1 ? 's' : ''}`}
          {' · une par ligne, '}{MAX_TARGETS}{' au maximum.'}
          {hint ? <> {hint}</> : null}
        </span>
        {invalid.length > 0 && (
          <span className="settings-recipient-hint__error">
            ⚠ {invalid.length > 1 ? 'Entrées invalides' : 'Entrée invalide'} : {invalid.join(', ')}
          </span>
        )}
        {tooMany && (
          <span className="settings-recipient-hint__error">⚠ {value.length} saisies, {MAX_TARGETS} au maximum.</span>
        )}
      </span>
    </label>
  )
})
